import {
  JiraCreateIssueMetadataResponse,
  JiraFieldOptionsParams,
  JiraFieldOptionsResponse,
  JiraMetadataFieldParams,
  TicketingFieldOptionsParams,
  TicketingFieldOptionsResponse,
  TicketingMetadataParams,
  TicketingMetadataResponse
} from "@/api/useJira.types"
import { RESOURCES_NAMES } from "@/const/resource.constants"
import { Config, Params, useCreate, useHttp } from "@/hooks/useHttp"

import type { VendorTicketingSystem } from "@/domain/ticketing"

export const useGetJiraFieldOptions = <T = JiraFieldOptionsResponse, S = T>(
  params: Params<JiraFieldOptionsParams>,
  config?: Config<T, S>
) => {
  const context = useHttp<T, S>(
    `${RESOURCES_NAMES.TICKETING.JIRA}/fields`,
    params,
    config
  )

  return context
}

export const useGetJiraCreateIssueMetadata = <
  T = JiraCreateIssueMetadataResponse,
  S = T
>(
  params: Params<JiraMetadataFieldParams> = {},
  config?: Config<T, S>
) => {
  const context = useHttp<T, S>(
    `${RESOURCES_NAMES.TICKETING.JIRA}/metadata`,
    params,
    config
  )

  return context
}
export const useCreateJiraIssue = (params: Params<object> = {}) => {
  const context = useCreate<
    { data: Jira.CreateIssuePayload },
    Jira.CreateIssuePayload // should be Jira.CreateIssueResponse and if error Jira.CreateIssueError
  >(`${RESOURCES_NAMES.ALERTS}/issues`, params, (_, newData) => ({
    data: newData
  }))

  return context
}

// An unreachable vendor (503) is shown in the ticket modal rather than as a toast
const VENDOR_UNAVAILABLE_STATUSES = [503]
// GitHub refuses a repository (400) with a message the modal shows instead
const METADATA_SILENT_STATUSES: Partial<
  Record<VendorTicketingSystem, number[]>
> = { github: [400, ...VENDOR_UNAVAILABLE_STATUSES] }

export const useGetTicketingMetadata = (
  system: VendorTicketingSystem,
  params: Params<TicketingMetadataParams> = {},
  config?: Config<TicketingMetadataResponse>
) =>
  useHttp<TicketingMetadataResponse>(
    RESOURCES_NAMES.TICKETING.METADATA(system),
    params,
    {
      silentErrorStatuses:
        METADATA_SILENT_STATUSES[system] ?? VENDOR_UNAVAILABLE_STATUSES,
      ...config
    }
  )

export const useGetTicketingFieldOptions = (
  system: VendorTicketingSystem,
  params: Params<TicketingFieldOptionsParams>,
  config?: Config<TicketingFieldOptionsResponse>
) =>
  useHttp<TicketingFieldOptionsResponse>(
    RESOURCES_NAMES.TICKETING.FIELDS(system),
    params,
    config
  )
